import { useMemo } from "react";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useSupportCenter } from "../../contexts/SupportCenterContext";
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./HelpCenterPage.module.css";

const FAQ_CONFIG = [
  {
    key: "upgradeSubscription",
    defaultQuestion: "How do I upgrade my subscription?",
    defaultAnswer:
      "Open Subscription from Settings, then enter your code or select a plan to upgrade.",
  },
  {
    key: "cancelSubscription",
    defaultQuestion: "Can I cancel my subscription?",
    defaultAnswer:
      "Yes. Go to Subscription -> Manage -> Cancel. Your access continues until the end of the billing period.",
  },
  {
    key: "applyPromoCode",
    defaultQuestion: "How do I apply a promo/activation code?",
    defaultAnswer:
      "In Subscription, paste your code (e.g., ZEQI-PREM-XXXX) and tap Apply.",
  },
  {
    key: "resetPassword",
    defaultQuestion: "How do I reset my password?",
    defaultAnswer:
      "On the login screen, tap 'Forgot Password' and follow the email instructions.",
  },
  {
    key: "multiDeviceUse",
    defaultQuestion: "Can I use Zeqi on multiple devices?",
    defaultAnswer: "Yes. Log in with the same account on each device.",
  },
  {
    key: "reportsRefresh",
    defaultQuestion: "My reports did not refresh - what should I do?",
    defaultAnswer:
      "Refresh on the Reports screen. If it persists, message support with the shop domain and a brief description.",
  },
  {
    key: "dataRefreshCadence",
    defaultQuestion: "How often does Zeqi refresh data?",
    defaultAnswer:
      "Free: limited previews. General: refresh on demand. Premium: priority refresh cadence.",
  },
  {
    key: "reportBugFeature",
    defaultQuestion: "How do I report a bug or request a feature?",
    defaultAnswer:
      "From the menu, choose Report a Bug or Request a Feature, describe the issue/idea, and send.",
  },
  {
    key: "dataStorage",
    defaultQuestion: "Does Zeqi store my data?",
    defaultAnswer:
      "We store only what is needed to operate your workspace and features. See our privacy policy for details.",
  },
  {
    key: "voiceInput",
    defaultQuestion: "Voice input is not working.",
    defaultAnswer:
      "Ensure microphone permissions are granted in your browser settings, then try again.",
  },
  {
    key: "notifications",
    defaultQuestion: "I am not receiving notifications.",
    defaultAnswer:
      "Enable notifications for Zeqi in system settings and inside Zeqi's Notification preferences.",
  },
];

const HelpCenterPage = () => {
  const { t, locale } = useLocalization();
  const support = useSupportCenter();
  const isRtl = locale === "ar";

  const copy = useMemo(
    () => ({
      title: t("HelpCenterScreen.title", { defaultValue: "Zeqi Help Center" }),
      faqTitle: t("HelpCenterScreen.faqTitle", {
        defaultValue: "Frequently Asked Questions",
      }),
      moreHelpTitle: t("HelpCenterScreen.moreHelpTitle", {
        defaultValue: "Need More Help?",
      }),
      messageSupport: t("HelpCenterScreen.messageSupport", {
        defaultValue: "Message Support",
      }),
      noticeText: t("HelpCenterScreen.noticeText", {
        defaultValue:
          "Free users have limited access. Upgrade to unlock premium features.",
      }),
    }),
    [t],
  );

  const faqs = useMemo(
    () =>
      FAQ_CONFIG.map(({ key, defaultQuestion, defaultAnswer }) => ({
        q: t(`HelpCenterScreen.faqs.${key}.question`, {
          defaultValue: defaultQuestion,
        }),
        a: t(`HelpCenterScreen.faqs.${key}.answer`, {
          defaultValue: defaultAnswer,
        }),
      })),
    [t],
  );

  return (
    <section className={pageStyles.page} data-page="help-center">
      <header className={pageStyles.sectionHeader}>
        <h2 className={styles.title}>{copy.title}</h2>
      </header>

      <div className={styles.section}>
        <h3 className={`${styles.sectionTitle} ${isRtl ? styles.rtl : ""}`}>
          {copy.faqTitle}
        </h3>
        <div className={styles.faqGrid}>
          {faqs.map((item, index) => (
            <div key={`${item.q}-${index}`} className={styles.faqCard}>
              <p className={`${styles.faqQuestion} ${isRtl ? styles.rtl : ""}`}>
                {item.q}
              </p>
              <p className={`${styles.faqAnswer} ${isRtl ? styles.rtl : ""}`}>
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={`${styles.sectionTitle} ${isRtl ? styles.rtl : ""}`}>
          {copy.moreHelpTitle}
        </h3>
        <button
          type="button"
          className={styles.supportButton}
          onClick={() => support?.openSupport?.()}
        >
          {copy.messageSupport}
        </button>
      </div>

      <div className={styles.noticeBox}>
        <span className={styles.noticeIcon}>i</span>
        <p className={`${styles.noticeText} ${isRtl ? styles.rtl : ""}`}>
          {copy.noticeText}
        </p>
      </div>
    </section>
  );
};

export default HelpCenterPage;
